import React, {createContext, useContext, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';

const NavigationGuardContext = createContext(null);

/**
 * ref-based navigation guard that never triggers re-renders.
 * SuggestionsContext registers a guard; any component can call runGuarded().
 */
export const NavigationGuardProvider = ({children}) => {
  const guardRef = useRef(null);

  const registerNavigationGuard = useCallback((guardFn) => {
    guardRef.current = guardFn;
    return () => {
      guardRef.current = null;
    };
  }, []);

  const runGuarded = useCallback((action) => {
    if (guardRef.current) {
      guardRef.current(action);
    } else {
      action();
    }
  }, []);

  // consumers never re-render from this context.
  const value = useRef({registerNavigationGuard, runGuarded}).current;

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
    </NavigationGuardContext.Provider>
  );
};

NavigationGuardProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useNavigationGuard = () => {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) {
    return {
      runGuarded: (action) => action(),
      registerNavigationGuard: () => () => {},
    };
  }
  return ctx;
};
